/**
 * Tray expression standard library — ONE canonical source, two runtimes
 * (ADR-0018). Spreadsheet-style formula functions familiar to non-developers
 * (IF, SUM, CONCAT, ROUND, FORMATDATE, …).
 *
 * This file is plain, dependency-free ES that is consumed TWO ways:
 *   - Backend (workflows): the QuickJS layer (`hub.workflow.js_expr_layer`)
 *     reads this text, strips the `export ` keywords, and evals it ONCE per
 *     context so the functions become globals callable from user expressions.
 *   - Frontend (apps/portal): imported via the `@shared/formulas` alias and
 *     registered into the sandboxed `lib/pageExpr` interpreter + declared in the
 *     IntelliSense `.d.ts` (workflow editor + page builder).
 *
 * So `{{ SUM($items.map(i => i.amount)) }}` evaluates identically in a workflow
 * node (server, QuickJS) and an app page (browser, pageExpr). Keep it pure (no
 * I/O, no host access) and stick to syntax both QuickJS and the bundler accept
 * (no TypeScript): the backend evals the raw text.
 */

// --- coercion helpers (not exported: internal only) -------------------------

function _toNum(x) {
  if (typeof x === "number") return x;
  if (x === null || x === undefined || x === "") return NaN;
  return Number(x);
}
function _toStr(x) {
  return x === null || x === undefined ? "" : String(x);
}
function _nums(args) {
  return args
    .flat(Infinity)
    .map(_toNum)
    .filter(function (n) {
      return !Number.isNaN(n);
    });
}
function _asDate(x) {
  if (x instanceof Date) return Number.isNaN(x.getTime()) ? null : x;
  if (typeof x === "number") {
    var dn = new Date(x);
    return Number.isNaN(dn.getTime()) ? null : dn;
  }
  if (typeof x === "string" && x.trim() !== "") {
    var ds = new Date(x);
    return Number.isNaN(ds.getTime()) ? null : ds;
  }
  return null;
}
function _pad(n) {
  return String(n).padStart(2, "0");
}
function _isBlank(x) {
  return x === null || x === undefined || x === "";
}
function _formatDate(value, fmt) {
  var d = _asDate(value);
  if (!d) return "";
  var map = {
    YYYY: String(d.getFullYear()),
    MM: _pad(d.getMonth() + 1),
    DD: _pad(d.getDate()),
    HH: _pad(d.getHours()),
    mm: _pad(d.getMinutes()),
    ss: _pad(d.getSeconds()),
  };
  return (fmt || "YYYY-MM-DD").replace(/YYYY|MM|DD|HH|mm|ss/g, function (t) {
    return map[t] !== undefined ? map[t] : t;
  });
}

// --- Logic ------------------------------------------------------------------

export function IF(cond, then, otherwise) {
  return cond ? then : otherwise === undefined ? null : otherwise;
}
export function AND() {
  return Array.prototype.slice.call(arguments).flat(Infinity).every(Boolean);
}
export function OR() {
  return Array.prototype.slice.call(arguments).flat(Infinity).some(Boolean);
}
export function NOT(x) {
  return !x;
}
export function ISBLANK(x) {
  return _isBlank(x);
}
export function COALESCE() {
  var found = Array.prototype.slice
    .call(arguments)
    .flat(Infinity)
    .find(function (a) {
      return !_isBlank(a);
    });
  return found === undefined ? null : found;
}

// --- Math -------------------------------------------------------------------

export function SUM() {
  return _nums(Array.prototype.slice.call(arguments)).reduce(function (a, b) {
    return a + b;
  }, 0);
}
export function AVERAGE() {
  var n = _nums(Array.prototype.slice.call(arguments));
  return n.length
    ? n.reduce(function (a, b) {
        return a + b;
      }, 0) / n.length
    : 0;
}
export function MIN() {
  var n = _nums(Array.prototype.slice.call(arguments));
  return n.length ? Math.min.apply(Math, n) : 0;
}
export function MAX() {
  var n = _nums(Array.prototype.slice.call(arguments));
  return n.length ? Math.max.apply(Math, n) : 0;
}
export function COUNT() {
  return _nums(Array.prototype.slice.call(arguments)).length;
}
export function ROUND(x, digits) {
  var n = _toNum(x);
  if (Number.isNaN(n)) return 0;
  var f = Math.pow(10, Math.trunc(_toNum(digits) || 0));
  return Math.round(n * f) / f;
}
export function ABS(x) {
  return Math.abs(_toNum(x)) || 0;
}
export function CEILING(x) {
  return Math.ceil(_toNum(x)) || 0;
}
export function FLOOR(x) {
  return Math.floor(_toNum(x)) || 0;
}

// --- Text -------------------------------------------------------------------

export function CONCAT() {
  return Array.prototype.slice.call(arguments).flat(Infinity).map(_toStr).join("");
}
export function JOIN(sep) {
  return Array.prototype.slice
    .call(arguments, 1)
    .flat(Infinity)
    .map(_toStr)
    .join(_toStr(sep));
}
export function UPPER(x) {
  return _toStr(x).toUpperCase();
}
export function LOWER(x) {
  return _toStr(x).toLowerCase();
}
export function TRIM(x) {
  return _toStr(x).trim();
}
export function LEN(x) {
  return _toStr(x).length;
}
export function LEFT(x, n) {
  return _toStr(x).slice(0, Math.max(0, _toNum(n) || 0));
}
export function RIGHT(x, n) {
  var s = _toStr(x);
  var k = Math.max(0, _toNum(n) || 0);
  return k === 0 ? "" : s.slice(-k);
}
export function CONTAINS(text, sub) {
  return _toStr(text).includes(_toStr(sub));
}
export function REPLACE(text, find, repl) {
  return _toStr(text).split(_toStr(find)).join(_toStr(repl));
}

// --- Date -------------------------------------------------------------------

export function TODAY() {
  return _formatDate(new Date(), "YYYY-MM-DD");
}
export function NOW() {
  return _formatDate(new Date(), "YYYY-MM-DD HH:mm:ss");
}
export function FORMATDATE(value, fmt) {
  return _formatDate(value, _toStr(fmt) || "YYYY-MM-DD");
}
export function YEAR(value) {
  var d = _asDate(value);
  return d ? d.getFullYear() : 0;
}
export function MONTH(value) {
  var d = _asDate(value);
  return d ? d.getMonth() + 1 : 0;
}
export function DAY(value) {
  var d = _asDate(value);
  return d ? d.getDate() : 0;
}

// --- Predicates (power the type-aware condition builder) --------------------
// Host functions, so they reach RegExp / Date even though the sandboxed page
// interpreter can't — the SAME compiled expression then works in both runtimes.

export function MATCHES(text, pattern, flags) {
  try {
    return new RegExp(String(pattern), flags ? String(flags) : undefined).test(_toStr(text));
  } catch (_e) {
    return false; // invalid regex → no match (never throws into the sandbox)
  }
}
export function DATEVAL(value) {
  var d = _asDate(value);
  return d ? d.getTime() : NaN; // NaN comparisons are always false → safe
}
export function DAYS_AGO(days) {
  var n = _toNum(days);
  if (Number.isNaN(n)) return NaN;
  return new Date().getTime() - n * 86400000; // now − n days, as a timestamp
}

/** Name → implementation map (for the frontend interpreter registration). */
export const FORMULAS = {
  IF: IF, AND: AND, OR: OR, NOT: NOT, ISBLANK: ISBLANK, COALESCE: COALESCE,
  SUM: SUM, AVERAGE: AVERAGE, MIN: MIN, MAX: MAX, COUNT: COUNT, ROUND: ROUND,
  ABS: ABS, CEILING: CEILING, FLOOR: FLOOR,
  CONCAT: CONCAT, JOIN: JOIN, UPPER: UPPER, LOWER: LOWER, TRIM: TRIM, LEN: LEN,
  LEFT: LEFT, RIGHT: RIGHT, CONTAINS: CONTAINS, REPLACE: REPLACE,
  TODAY: TODAY, NOW: NOW, FORMATDATE: FORMATDATE, YEAR: YEAR, MONTH: MONTH, DAY: DAY,
  MATCHES: MATCHES, DATEVAL: DATEVAL, DAYS_AGO: DAYS_AGO,
};

/** TS declarations + docs — fed into the IntelliSense `.d.ts` (both editors). */
export const FORMULA_DEFS = [
  { name: "IF", decl: "declare function IF(condition: any, then: any, otherwise?: any): any;", doc: "Return `then` when the condition is truthy, else `otherwise`." },
  { name: "AND", decl: "declare function AND(...values: any[]): boolean;", doc: "True when every value is truthy." },
  { name: "OR", decl: "declare function OR(...values: any[]): boolean;", doc: "True when any value is truthy." },
  { name: "NOT", decl: "declare function NOT(value: any): boolean;", doc: "Logical negation." },
  { name: "ISBLANK", decl: "declare function ISBLANK(value: any): boolean;", doc: "True when null / undefined / empty string." },
  { name: "COALESCE", decl: "declare function COALESCE(...values: any[]): any;", doc: "First non-blank value." },
  { name: "SUM", decl: "declare function SUM(...values: any[]): number;", doc: "Sum of numbers (arrays flattened; text ignored)." },
  { name: "AVERAGE", decl: "declare function AVERAGE(...values: any[]): number;", doc: "Arithmetic mean of numbers." },
  { name: "MIN", decl: "declare function MIN(...values: any[]): number;", doc: "Smallest number." },
  { name: "MAX", decl: "declare function MAX(...values: any[]): number;", doc: "Largest number." },
  { name: "COUNT", decl: "declare function COUNT(...values: any[]): number;", doc: "How many values are numbers." },
  { name: "ROUND", decl: "declare function ROUND(value: any, digits?: number): number;", doc: "Round to `digits` decimals (default 0)." },
  { name: "ABS", decl: "declare function ABS(value: any): number;", doc: "Absolute value." },
  { name: "CEILING", decl: "declare function CEILING(value: any): number;", doc: "Round up to an integer." },
  { name: "FLOOR", decl: "declare function FLOOR(value: any): number;", doc: "Round down to an integer." },
  { name: "CONCAT", decl: "declare function CONCAT(...values: any[]): string;", doc: "Join values into one string." },
  { name: "JOIN", decl: "declare function JOIN(separator: any, ...values: any[]): string;", doc: "Join values with a separator." },
  { name: "UPPER", decl: "declare function UPPER(value: any): string;", doc: "Uppercase." },
  { name: "LOWER", decl: "declare function LOWER(value: any): string;", doc: "Lowercase." },
  { name: "TRIM", decl: "declare function TRIM(value: any): string;", doc: "Trim surrounding whitespace." },
  { name: "LEN", decl: "declare function LEN(value: any): number;", doc: "String length." },
  { name: "LEFT", decl: "declare function LEFT(value: any, n: number): string;", doc: "First `n` characters." },
  { name: "RIGHT", decl: "declare function RIGHT(value: any, n: number): string;", doc: "Last `n` characters." },
  { name: "CONTAINS", decl: "declare function CONTAINS(text: any, sub: any): boolean;", doc: "True when `text` contains `sub`." },
  { name: "REPLACE", decl: "declare function REPLACE(text: any, find: any, replacement: any): string;", doc: "Replace all occurrences of `find`." },
  { name: "TODAY", decl: "declare function TODAY(): string;", doc: "Today as YYYY-MM-DD." },
  { name: "NOW", decl: "declare function NOW(): string;", doc: "Now as YYYY-MM-DD HH:mm:ss." },
  { name: "FORMATDATE", decl: "declare function FORMATDATE(value: any, format?: string): string;", doc: "Format a date (YYYY MM DD HH mm ss tokens)." },
  { name: "YEAR", decl: "declare function YEAR(value: any): number;", doc: "4-digit year of a date." },
  { name: "MONTH", decl: "declare function MONTH(value: any): number;", doc: "Month (1–12) of a date." },
  { name: "DAY", decl: "declare function DAY(value: any): number;", doc: "Day of month of a date." },
  { name: "MATCHES", decl: "declare function MATCHES(text: any, pattern: string, flags?: string): boolean;", doc: "True when `text` matches the regular expression `pattern`." },
  { name: "DATEVAL", decl: "declare function DATEVAL(value: any): number;", doc: "Date/time as a timestamp (ms) for comparison; NaN when unparseable." },
  { name: "DAYS_AGO", decl: "declare function DAYS_AGO(days: number): number;", doc: "Timestamp (ms) of `days` ago from now — for relative date filters (negative = future)." },
];
